import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Schedule, AccessTime, Save, Add, Edit, Delete } from '@mui/icons-material';
import api from '../services/api';

interface BusinessHour {
  id?: number;
  day_of_week: number;
  open_time: string | null;
  close_time: string | null;
  is_working_day: boolean;
  timezone: string;
  created_at?: string;
  updated_at?: string;
}

interface AutoResponder {
  id?: number;
  name: string;
  trigger_type: string;
  message: string;
  conditions: Record<string, any>;
  is_enabled: boolean;
  delay_seconds: number;
  times_triggered?: number;
  created_at?: string;
  updated_at?: string;
}

const DAYS = [
  'Monday',
  'Tuesday', 
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney'
];

const TRIGGER_TYPES = [
  { value: 'outside_business_hours', label: 'Outside Business Hours' },
  { value: 'first_message', label: 'First Message' },
  { value: 'no_agent_available', label: 'No Agent Available' }
];

const BusinessHoursPage: React.FC = () => {
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [autoResponders, setAutoResponders] = useState<AutoResponder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingResponder, setEditingResponder] = useState<AutoResponder | null>(null);
  const [showAddResponder, setShowAddResponder] = useState(false);

  // Initialize business hours for all days
  const initializeBusinessHours = (): BusinessHour[] => {
    return Array.from({ length: 7 }, (_, index) => ({
      day_of_week: index,
      open_time: index < 5 ? '09:00' : null, // Monday-Friday default to 9 AM
      close_time: index < 5 ? '17:00' : null, // Monday-Friday default to 5 PM
      is_working_day: index < 5, // Monday-Friday are working days
      timezone: 'UTC'
    }));
  };

  useEffect(() => {
    fetchBusinessHours();
    fetchAutoResponders();
  }, []);

  const fetchBusinessHours = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-hours/');
      if (response.data.length === 0) {
        // If no business hours exist, initialize with defaults
        setBusinessHours(initializeBusinessHours());
      } else {
        // Fill in missing days
        const existingDays = response.data.map((bh: BusinessHour) => bh.day_of_week);
        const allDays = initializeBusinessHours();
        const mergedHours = allDays.map(defaultHour => {
          const existing = response.data.find((bh: BusinessHour) => bh.day_of_week === defaultHour.day_of_week);
          return existing || defaultHour;
        });
        setBusinessHours(mergedHours);
      }
    } catch (error: any) {
      setError('Failed to fetch business hours');
      console.error('Error fetching business hours:', error);
      // Initialize with defaults on error
      setBusinessHours(initializeBusinessHours());
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoResponders = async () => {
    try {
      const response = await api.get('/business-hours/auto-responders');
      setAutoResponders(response.data);
    } catch (error: any) {
      console.error('Error fetching auto responders:', error);
    }
  };

  const handleBusinessHourChange = (dayIndex: number, field: keyof BusinessHour, value: any) => {
    setBusinessHours(prev => prev.map((bh, index) => 
      index === dayIndex ? { ...bh, [field]: value } : bh
    ));
  };

  const saveBusinessHours = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/business-hours/bulk', businessHours);
      setSuccess('Business hours saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchBusinessHours(); // Refresh to get IDs
    } catch (error: any) {
      setError('Failed to save business hours');
      console.error('Error saving business hours:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAutoResponderSubmit = async (responderData: Partial<AutoResponder>) => {
    try {
      if (editingResponder?.id) {
        await api.put(`/business-hours/auto-responders/${editingResponder.id}`, responderData);
        setSuccess('Auto responder updated successfully');
      } else {
        await api.post('/business-hours/auto-responders', responderData);
        setSuccess('Auto responder created successfully');
      }
      
      fetchAutoResponders();
      setEditingResponder(null);
      setShowAddResponder(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to save auto responder');
      console.error('Error saving auto responder:', error);
    }
  };

  const deleteAutoResponder = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this auto responder?')) return;
    
    try {
      await api.delete(`/business-hours/auto-responders/${id}`);
      setSuccess('Auto responder deleted successfully');
      fetchAutoResponders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to delete auto responder');
      console.error('Error deleting auto responder:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={1}>
        <Schedule />
        Business Hours
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Business Hours Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Working Hours Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set your business hours for each day of the week. Customers will see an away message outside these hours.
        </Typography>

        <Grid container spacing={2}>
          {businessHours.map((hour, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {DAYS[index]}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hour.is_working_day}
                          onChange={(e) => handleBusinessHourChange(index, 'is_working_day', e.target.checked)}
                        />
                      }
                      label="Working day"
                    />
                  </Box>
                  
                  {hour.is_working_day && (
                    <Box>
                      <Box display="flex" gap={2} mb={2}>
                        <TextField
                          type="time"
                          label="Open Time"
                          value={hour.open_time || ''}
                          onChange={(e) => handleBusinessHourChange(index, 'open_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          type="time"
                          label="Close Time"
                          value={hour.close_time || ''}
                          onChange={(e) => handleBusinessHourChange(index, 'close_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          fullWidth
                        />
                      </Box>
                      
                      <FormControl fullWidth size="small">
                        <InputLabel>Timezone</InputLabel>
                        <Select
                          value={hour.timezone}
                          label="Timezone"
                          onChange={(e) => handleBusinessHourChange(index, 'timezone', e.target.value)}
                        >
                          {TIMEZONES.map(tz => (
                            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  
                  {!hour.is_working_day && (
                    <Box textAlign="center" py={2}>
                      <Typography variant="body2" color="text.secondary">
                        Not a working day
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={saveBusinessHours}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Business Hours'}
          </Button>
        </Box>
      </Paper>

      {/* Auto Responders Section */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Auto Responders
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setShowAddResponder(true)}
          >
            Add Auto Responder
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure automatic responses for different scenarios like outside business hours.
        </Typography>

        {autoResponders.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No auto responders configured. Add one to get started.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {autoResponders.map((responder) => (
              <Grid item xs={12} md={6} lg={4} key={responder.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {responder.name}
                      </Typography>
                      <Box>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => setEditingResponder(responder)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => responder.id && deleteAutoResponder(responder.id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                    
                    <Chip
                      label={TRIGGER_TYPES.find(t => t.value === responder.trigger_type)?.label || responder.trigger_type}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {responder.message}
                    </Typography>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <FormControlLabel
                        control={<Switch checked={responder.is_enabled} />}
                        label="Enabled"
                        disabled
                      />
                      {responder.times_triggered !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          Triggered {responder.times_triggered} times
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Auto Responder Form Dialog would go here */}
      {(showAddResponder || editingResponder) && (
        <AutoResponderForm
          responder={editingResponder}
          onSubmit={handleAutoResponderSubmit}
          onCancel={() => {
            setEditingResponder(null);
            setShowAddResponder(false);
          }}
        />
      )}
    </Box>
  );
};

interface AutoResponderFormProps {
  responder: AutoResponder | null;
  onSubmit: (data: Partial<AutoResponder>) => void;
  onCancel: () => void;
}

const AutoResponderForm: React.FC<AutoResponderFormProps> = ({ responder, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Partial<AutoResponder>>({
    name: responder?.name || '',
    trigger_type: responder?.trigger_type || 'outside_business_hours',
    message: responder?.message || '',
    conditions: responder?.conditions || {},
    is_enabled: responder?.is_enabled ?? true,
    delay_seconds: responder?.delay_seconds || 0
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {responder ? 'Edit Auto Responder' : 'Add Auto Responder'}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Trigger Type</InputLabel>
            <Select
              value={formData.trigger_type}
              label="Trigger Type"
              onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
            >
              {TRIGGER_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            multiline
            rows={4}
            fullWidth
            required
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Delay (seconds)"
            type="number"
            value={formData.delay_seconds}
            onChange={(e) => setFormData({ ...formData, delay_seconds: parseInt(e.target.value) || 0 })}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_enabled}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              />
            }
            label="Enabled"
          />
        </Grid>
      </Grid>
      
      <Box mt={3} display="flex" gap={2}>
        <Button variant="contained" onClick={handleSubmit}>
          {responder ? 'Update' : 'Create'} Auto Responder
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default BusinessHoursPage;